import { FileOutlined } from '@ant-design/icons'
import type { ManualAccessorial } from '@api/useFreight/types'
import { BBDTag, Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'

import type * as React from 'react'
import { ActiveStatusRenderer, type BaseRate } from '../../components/ActiveStatusRenderer'
import type { Context, GridApi } from 'ag-grid-community'

/**
 * Renders a switch for the active status of an accessorial with popconfirm
 */
export function ActiveRenderer({
  value,
  data,
  api,
  context,
}: {
  value: boolean
  data: ManualAccessorial
  api: GridApi
  context: Context
}) {
  const queryClient = useQueryClient()

  return (
    <ActiveStatusRenderer
      value={value}
      data={data as unknown as BaseRate}
      rateType='manualAccessorial'
      context={context}
      api={api}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: ['manual_accessorials'] })
      }}
    />
  )
}

/**
 * Renders location information, showing "Any" when appropriate
 */
export function LocationRenderer({ value }: { value: string | null }) {
  if (!value || value?.toLowerCase() === 'any') {
    return <Texto appearance='hint'>Any</Texto>
  }
  return <Texto>{value}</Texto>
}

/**
 * Renders product group information, showing "Any" when null
 */
export function ProductGroupRenderer({ value }: { value: string | null }) {
  if (value === null || !value) {
    return <Texto appearance='hint'>Any</Texto>
  }
  return <Texto>{value}</Texto>
}

/**
 * Renders fee type with appropriate icon - matches automatic accessorials
 */
export function FeeTypeRenderer({ value }: { value?: { type: string } }) {
  if (!value) return null

  // Define icons for each fee type
  const feeTypeIcons: Record<string, React.ReactNode> = {
    'Per Order': <FileOutlined />,
    'Per Order Load': <MaterialIcon icon='upload' />,
    'Per Order Drop': <MaterialIcon icon='download' />,
    'Per Order Stop': <MaterialIcon icon='location_on' />,
    'Per Order Mile': <MaterialIcon icon='route' />,
    'Per Order Hour': <MaterialIcon icon='schedule' />,
    'Per Order Gallon': <MaterialIcon icon='local_gas_station' />,
    'Base Freight %': <MaterialIcon icon='percent' />,
    'Base & Accessorial %': <MaterialIcon icon='percent' />,
    'Total Freight %': <MaterialIcon icon='percent' />,
    'Per Event': <MaterialIcon icon='event' />,
    'Per Stop': <MaterialIcon icon='location_on' />,
    'Per Drop': <MaterialIcon icon='download' />,
    'Per Load': <MaterialIcon icon='upload' />,
    'Per Mile': <MaterialIcon icon='route' />,
    'Per Hour': <MaterialIcon icon='schedule' />,
    'Per Volume': <MaterialIcon icon='local_gas_station' />,
    'Per Trigger': <MaterialIcon icon='bolt' />,
    'Above Min': <MaterialIcon icon='trending_up' />,
  }

  // Get the icon based on the fee type, or use a default
  const icon = feeTypeIcons[value.type] || <MaterialIcon icon='attach_money' />

  // Handle the display text based on fee type
  let displayText: { prefix?: string; main: string; showPercent?: boolean } = {
    main: value.type,
  }

  if (value.type.startsWith('Per ')) {
    displayText = {
      prefix: 'Per',
      main: value.type.substring(4),
    }
  } else if (value.type === 'Base Freight %') {
    displayText = {
      main: 'Base Freight',
      prefix: '',
    }
  } else if (value.type === 'Base & Accessorial %') {
    displayText = {
      prefix: 'Base & Accessorial',
      main: '',
    }
  } else if (value.type === 'Total Freight %') {
    displayText = {
      main: 'Total Freight',
      prefix: '',
    }
  }

  return (
    <Horizontal gap={8} verticalCenter>
      {icon}
      <Horizontal gap={2}>
        {displayText.prefix && <Texto>{displayText.prefix}</Texto>}
        <Texto weight='bold'>{displayText.main}</Texto>
        {displayText.showPercent && (
          <Texto category='label' appearance='hint'>
            %
          </Texto>
        )}
      </Horizontal>
    </Horizontal>
  )
}

/**
 * Renders rate with proper formatting
 */
export function RateRenderer({ value, data }: { value: number | null; data: ManualAccessorial }) {
  if (value === null || value === undefined) {
    return <Texto appearance='hint'>-</Texto>
  }

  const feeType = data?.fee_type?.type || ''
  const isPercentageType = isPercentageFeeType(feeType)

  if (isPercentageType) {
    // For percentage types, the API stores as decimal (0.20 for 20%)
    // So we multiply by 100 to display as percentage
    const percentValue = (value * 100).toFixed(2)
    return (
      <Texto weight='bold' style={{ textAlign: 'right', width: '100%' }}>
        {percentValue}%
      </Texto>
    )
  }

  // For non-percentage types, display as currency
  return (
    <Texto weight='bold' style={{ textAlign: 'right', width: '100%' }}>
      ${value.toFixed(5)}
    </Texto>
  )
}

/**
 * Renders approval as a tag
 */
export function ApprovalRenderer({ value }: { value: boolean }) {
  if (!value) {
    return <Texto appearance='hint'>No</Texto>
  }
  return (
    <BBDTag style={{ width: 'fit-content' }} warning color='warning'>
      REQ
    </BBDTag>
  )
}

/**
 * Renders reason code required as a tag
 */
export function ReasonCodeRenderer({ value }: { value: boolean }) {
  if (!value) {
    return <Texto appearance='hint'>No</Texto>
  }
  return (
    <BBDTag style={{ width: 'fit-content' }} warning color='warning'>
      REQ
    </BBDTag>
  )
}

/**
 * Renders exclude from invoice as a tag
 */
export function ExcludeFromInvoiceRenderer({ value }: { value: boolean }) {
  if (!value) {
    return <Texto appearance='hint'>No</Texto>
  }
  return (
    <BBDTag style={{ width: 'fit-content' }} warning>
      EXC
    </BBDTag>
  )
}

/**
 * Renders the next scheduled rate date
 */
export function NextRateDateRenderer({ data }: { data: ManualAccessorial }) {
  if (!data.next_scheduled_rate) return null
  return <Texto>{new Date(data.next_scheduled_rate.effective_from).toLocaleDateString()}</Texto>
}

/**
 * Renders the effective date of the current rate
 */
export function EffectiveDateRenderer({ value, data }: { value: string; data: ManualAccessorial }) {
  // Get the current rate's effective date
  if (!data.rates || data.rates.length === 0) return null

  // Sort rates by effective_from date to get the most recent
  const sortedRates = [...data.rates].sort(
    (a, b) => new Date(b.effective_from).getTime() - new Date(a.effective_from).getTime()
  )

  const currentRate = sortedRates[0]
  return <Texto>{new Date(currentRate.effective_from).toLocaleDateString()}</Texto>
}

// Helper function to check if fee type is percentage based
const isPercentageFeeType = (type: string): boolean => {
  return type?.includes('%') || false
}
